
import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../context/AppContext';
import { 
  ArrowLeft, Clock, Calendar, Facebook, Twitter, 
  Linkedin as LinkedinIcon, Link as LinkIcon, ThumbsUp, MessageSquare, Send, Eye, Share2, Bookmark, Sparkles, CheckCircle2, ShieldAlert, Award
} from 'lucide-react';

const BlogPostDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { posts, comments, addComment, toggleLike, isLiked, currentUser, incrementViewCount, approvePost, hasPermission, registeredUsers, institutionalCALogo } = useApp();
  const [commentText, setCommentText] = useState('');
  const [showCopyAlert, setShowCopyAlert] = useState(false);
  
  const post = posts.find((p) => p.id === id);

  useEffect(() => {
    if (id) {
      incrementViewCount(id);
    }
  }, [id]);

  if (!post) return <Navigate to="/blog" replace />;

  const author = registeredUsers.find(u => u.id === post.authorId);
  const postComments = comments.filter(c => c.postId === post.id && (c.status === 'approved' || c.userId === currentUser?.id));
  const isCA = author?.designation?.includes('CA') || author?.designation?.includes('FCA');
  
  const handleComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || !currentUser) return;
    addComment(post.id, commentText);
    setCommentText('');
  };

  const shareOnSocial = (platform: 'facebook' | 'twitter' | 'linkedin') => {
    const url = window.location.href;
    const text = `Check out this financial insight by ${post.author}: "${post.title}"`;
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setShowCopyAlert(true);
    setTimeout(() => setShowCopyAlert(false), 3000);
  };

  return (
    <div className="min-h-screen bg-white relative text-slate-900">
      {/* Cinematic Hero Section */}
      <div className="relative h-[70vh] w-full overflow-hidden">
        <img 
          src={post.imageUrl || 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2000&auto=format&fit=crop'} 
          className="w-full h-full object-cover" 
          alt={post.title} 
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/20 via-slate-900/40 to-white" />
        
        <div className="absolute inset-0 flex flex-col justify-end pb-24 px-6">
          <div className="max-w-4xl mx-auto w-full">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <Link to="/blog" className="inline-flex items-center gap-2 text-indigo-100 hover:text-white font-black uppercase tracking-widest text-[10px] mb-12 transition-colors group px-6 py-3 bg-slate-900/40 backdrop-blur-xl rounded-2xl border border-white/20">
                <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                Knowledge Hub
              </Link>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap gap-3 mb-8">
              <span className="px-4 py-1.5 rounded-xl bg-indigo-600 text-white text-[10px] uppercase font-black tracking-widest shadow-xl">
                {post.category}
              </span>
              <span className="px-4 py-1.5 rounded-xl bg-white/90 backdrop-blur-md text-slate-700 text-[10px] uppercase font-black tracking-widest border border-slate-200 flex items-center gap-2 shadow-sm">
                <Clock size={12} /> {post.readingTime}
              </span>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="text-4xl md:text-7xl font-display font-black text-slate-900 mb-10 leading-[1.05] tracking-tight drop-shadow-sm"
            >
              {post.title}
            </motion.h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10 -mt-10">
        <article className="mb-20">
          <header className="mb-12">
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="flex flex-wrap items-center justify-between gap-8 pb-10 border-b border-slate-200"
            >
              <Link to={`/author/${post.authorId}`} className="flex items-center gap-4 group/author">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 text-xl font-black shadow-lg overflow-hidden border border-slate-200 group-hover/author:scale-105 transition-transform relative">
                  {author?.avatar ? (
                    <img src={author.avatar} alt={post.author} className="w-full h-full object-cover" />
                  ) : post.author.charAt(0)}
                  {isCA && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full border border-indigo-600 p-0.5 overflow-hidden shadow-lg">
                      <img src={institutionalCALogo} className="w-full h-full object-contain" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-slate-900 font-bold group-hover/author:text-indigo-600 transition-colors flex items-center gap-2">
                    {post.author}
                    {isCA && <Award size={16} className="text-indigo-600" />}
                  </div>
                  <div className="text-slate-500 text-sm flex items-center gap-2">
                    <Calendar size={14} /> {post.date}
                  </div>
                </div>
              </Link>

              <div className="flex items-center gap-6">
                 <div className="flex flex-col items-center">
                    <span className="text-slate-900 font-black text-xl">{post.views}</span>
                    <span className="text-slate-400 text-[9px] font-black uppercase tracking-widest">Views</span>
                 </div>
                 <div className="h-8 w-px bg-slate-200" />
                 <div className="flex flex-col items-center">
                    <span className="text-slate-900 font-black text-xl">{post.likes}</span>
                    <span className="text-slate-400 text-[9px] font-black uppercase tracking-widest">Likes</span>
                 </div>
                 <div className="h-8 w-px bg-slate-200" />
                 <div className="flex flex-col items-center">
                    <span className="text-slate-900 font-black text-xl">{post.shares || 0}</span>
                    <span className="text-slate-400 text-[9px] font-black uppercase tracking-widest">Shares</span>
                 </div>
              </div>
            </motion.div>
          </header>

          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="prose prose-slate prose-lg max-w-none text-slate-600 leading-relaxed space-y-8 font-medium pb-20 border-b border-slate-200"
          >
            {post.content.split('\n').map((paragraph, i) => (
              paragraph.trim() && <p key={i}>{paragraph}</p>
            ))}
          </motion.div>

          <footer className="mt-12 flex flex-wrap items-center justify-between gap-10">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => toggleLike(post.id)}
                className={`flex items-center gap-3 px-8 py-4 rounded-2xl font-bold transition-all shadow-xl ${isLiked(post.id) ? 'bg-indigo-600 text-white shadow-indigo-600/30' : 'bg-white text-slate-500 hover:text-indigo-600 hover:bg-slate-50 border border-slate-200'}`}
              >
                <ThumbsUp size={20} fill={isLiked(post.id) ? "currentColor" : "none"} />
                {isLiked(post.id) ? 'Liked' : 'Insightful'}
              </button>
              
              <div className="relative">
                <button 
                  onClick={copyLink}
                  className="p-4 bg-white border border-slate-200 rounded-2xl text-slate-400 hover:text-indigo-600 hover:bg-slate-50 transition-all shadow-xl active:scale-95"
                  title="Copy Direct Link"
                >
                  <LinkIcon size={24} />
                </button>
                <AnimatePresence>
                  {showCopyAlert && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10, x: '-50%' }} animate={{ opacity: 1, y: 0, x: '-50%' }} exit={{ opacity: 0 }}
                      className="absolute bottom-full left-1/2 mb-4 px-4 py-2 bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg whitespace-nowrap shadow-2xl"
                    >
                      Link Copied
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-slate-400 font-black uppercase text-[10px] tracking-widest">Distribute Analysis</span>
              <div className="flex gap-2">
                <button 
                  onClick={() => shareOnSocial('facebook')}
                  className="p-3 bg-white hover:bg-[#1877F2]/10 hover:text-[#1877F2] rounded-xl transition-all border border-slate-200 shadow-md active:scale-90"
                >
                  <Facebook size={18} />
                </button>
                <button 
                  onClick={() => shareOnSocial('twitter')}
                  className="p-3 bg-white hover:bg-[#1DA1F2]/10 hover:text-[#1DA1F2] rounded-xl transition-all border border-slate-200 shadow-md active:scale-90"
                >
                  <Twitter size={18} />
                </button>
                <button 
                  onClick={() => shareOnSocial('linkedin')}
                  className="p-3 bg-white hover:bg-[#0A66C2]/10 hover:text-[#0A66C2] rounded-xl transition-all border border-slate-200 shadow-md active:scale-90"
                >
                  <LinkedinIcon size={18} />
                </button>
              </div>
            </div>
          </footer>
        </article>

        {/* Discussion Section */}
        <section id="discussion" className="pt-20">
          <h3 className="text-3xl font-display font-black text-slate-900 mb-10 flex items-center gap-4">
            <MessageSquare className="text-indigo-600" size={28} />
            Institutional Dialogue ({postComments.length})
          </h3>
          
          <div className="space-y-12 mb-16">
            {postComments.length > 0 ? (
              postComments.map((comment) => (
                <div key={comment.id} className="flex gap-6 group">
                  <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-indigo-600 font-bold shrink-0 shadow-sm">
                    {comment.userName.charAt(0)}
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-slate-900 font-bold">{comment.userName}</span>
                      <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
                        <Clock size={10} /> {comment.date}
                      </span>
                    </div>
                    <p className="text-slate-600 leading-relaxed font-medium">
                      {comment.content}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-16 text-center bg-slate-50 rounded-[3rem] border border-dashed border-slate-200">
                <p className="text-slate-500 font-bold uppercase tracking-widest text-sm">No institutional dialogue recorded yet.</p>
              </div>
            )}
          </div>

          {currentUser ? (
            <div className="p-10 rounded-[3rem] bg-white border border-slate-200 shadow-2xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 text-indigo-50 group-hover:text-indigo-100 transition-colors">
                <Sparkles size={100} />
              </div>
              <h4 className="text-2xl font-display font-bold text-slate-900 mb-8">Contribute to Analysis</h4>
              <form onSubmit={handleComment} className="space-y-4 relative z-10">
                <textarea 
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Share your professional perspective..."
                  rows={4}
                  className="w-full px-6 py-5 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 outline-none focus:border-indigo-500 transition-all resize-none placeholder:text-slate-400"
                />
                <button 
                  type="submit"
                  className="px-10 py-5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl shadow-xl shadow-indigo-600/30 transition-all flex items-center gap-3 active:scale-[0.98]"
                >
                  Post Insight <Send size={18} />
                </button>
              </form>
            </div>
          ) : (
            <div className="p-12 rounded-[3rem] bg-slate-50 border border-slate-200 text-center">
              <p className="text-slate-500 font-bold mb-6">You must be registered to contribute to the discussion.</p>
              <button 
                onClick={() => { /* This would ideally trigger the AuthModal from a context or parent */ }}
                className="text-indigo-600 font-black uppercase tracking-[0.2em] text-xs hover:text-indigo-500 transition-colors"
              >
                Sign In to Participate
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default BlogPostDetail;
